import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import ProductCard from "./ProductCard";
import useStoreType from "../utils/storeTypes";

const AUTOPLAY_DELAY = 3200;

// One category row on the home page: an auto-scrolling product slider. `total` is how many products the category has
// (the row shows only its first ones); without it, the row's own count.
export default function Category({ cat, items = [], total, add, viewAll, onSelect, cart, updateQty }) {
    const count = total ?? items.length;
    const type = useStoreType();
    const trackRef = useRef(null);
    const timerRef = useRef(null);
    const [paused, setPaused] = useState(false);

    const getStep = () => {
        const track = trackRef.current;
        if (!track) return 280;
        const firstItem = track.querySelector(".categoryCarouselItem");
        if (!firstItem) return Math.max(track.clientWidth, 280);
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
        return firstItem.getBoundingClientRect().width + gap;
    };

    const move = (direction) => {
        const track = trackRef.current;
        if (!track) return;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 2) return;
        const step = getStep();
        const next = track.scrollLeft + step * direction;
        if (direction > 0 && track.scrollLeft >= maxScroll - 6) {
            track.scrollTo({ left: 0, behavior: "smooth" });
        } else if (direction < 0 && track.scrollLeft <= 6) {
            track.scrollTo({ left: maxScroll, behavior: "smooth" });
        } else {
            track.scrollTo({ left: Math.max(0, Math.min(next, maxScroll)), behavior: "smooth" });
        }
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track || items.length < 2 || paused) return undefined;

        const advance = () => {
            // Re-read dimensions each time so this remains reliable after responsive
            // layout changes, image loads, or a window resize.
            const currentTrack = trackRef.current;
            if (!currentTrack) return;
            const maxScroll = currentTrack.scrollWidth - currentTrack.clientWidth;
            if (maxScroll <= 2) return;

            if (currentTrack.scrollLeft >= maxScroll - 6) {
                currentTrack.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                const next = Math.min(currentTrack.scrollLeft + getStep(), maxScroll);
                currentTrack.scrollTo({ left: next, behavior: "smooth" });
            }
        };

        timerRef.current = window.setInterval(advance, AUTOPLAY_DELAY);
        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [cat, items.length, paused]);

    useEffect(() => {
        if (trackRef.current) trackRef.current.scrollTo({ left: 0, behavior: "auto" });
    }, [cat, items.length]);

    return (
        <section className="section">
            <div className="head">
                <div>
                    <small>CURATED FOR YOU</small>
                    <h2>{cat} <em>{count} {count === 1 ? type.item : type.items}</em></h2>
                </div>
                <div className="categoryCarouselActions">
                    <button type="button" className="categoryCarouselArrow" aria-label={`Previous ${cat} products`} onClick={() => move(-1)} disabled={items.length < 2}>‹</button>
                    <button type="button" className="categoryCarouselArrow" aria-label={`Next ${cat} products`} onClick={() => move(1)} disabled={items.length < 2}>›</button>
                    <Button variant="outline-success" className="viewAll" onClick={viewAll}>View all →</Button>
                </div>
            </div>
            {items.length ? (
                <div
                    className="categoryCarouselViewport"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocusCapture={() => setPaused(true)}
                    onBlurCapture={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
                    }}
                >
                    <div className="categoryCarouselTrack" ref={trackRef} aria-label={`${cat} products`} role="region" tabIndex={0}>
                        {items.map((p) => (
                            <div className="categoryCarouselItem" key={p.id}>
                                <ProductCard p={p} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="noResults">Nothing here yet.</div>
            )}
        </section>
    );
}
